import { useEffect, useMemo, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { Loader2, Plus, X, Smartphone, CheckCircle2, CircleDashed, Trash2, Edit2, Search } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { hasModule } from "@/lib/permissions";

const STATUS_STYLE = {
  available: { bg: "#D8F3DC", color: "#1B4332", label: "Available" },
  assigned: { bg: "#DCE9F0", color: "#1F4863", label: "Assigned" },
  lost: { bg: "#FADED7", color: "#9A3412", label: "Lost" },
  inactive: { bg: "#F5F5F4", color: "#57534E", label: "Inactive" },
};

const POOLS = [
  { value: "NAVARATNAA", label: "Navaratnaa" },
  { value: "NEWAGRICARE", label: "Newagricare" },
];

export default function MobileNumbers() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ total: 0, assigned: 0, available: 0, pools: {} });
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [pool, setPool] = useState("");
  const [status, setStatus] = useState("");
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);

  // A user can manage this page if they aren't an "employee", OR if an admin
  // has explicitly granted them the `mobile_numbers` module (which is what
  // brought them to this route in the first place). This lets HR/office
  // staff on the base "employee" role manage the SIM pool once given access.
  const canManage = user?.role !== "employee" || hasModule(user, "mobile_numbers");

  const load = async () => {
    setLoading(true);
    const params = {};
    if (pool) params.pool = pool;
    if (status) params.status = status;
    const [rowsRes, sumRes] = await Promise.all([
      api.get("/mobile-numbers", { params }),
      api.get("/mobile-numbers/summary"),
    ]);
    setItems(rowsRes.data);
    setSummary(sumRes.data);
    setLoading(false);
  };

  useEffect(() => {
    api.get("/employees")
       .then((r) => setEmployees(r.data || []))
       .catch(() => setEmployees([]));    // graceful fallback if perm denied
  }, []);
  useEffect(() => { load(); }, [pool, status]);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter((i) =>
      (i.number || "").toLowerCase().includes(q)
      || (i.provider || "").toLowerCase().includes(q)
      || (i.assigned_to_name || "").toLowerCase().includes(q)
      || (i.plan || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  const remove = async (id) => {
    if (!window.confirm("Delete this mobile number entry?")) return;
    await api.delete(`/mobile-numbers/${id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="mobile-numbers-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="kpi-label" style={{ color: "var(--color-secondary)" }}>Fleet & Facilities</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Mobile Numbers</h1>
          <p className="text-stone-600 mt-1 text-sm">Company SIM / mobile-number inventory and assignments — pools: Navaratnaa & Newagricare.</p>
        </div>
        {canManage && (
          <button onClick={() => { setEditing(null); setShow(true); }} data-testid="add-mobile-button"
                  className="flex items-center gap-2 px-4 py-2 rounded-md text-white font-medium" style={{ background: "var(--color-primary)" }}>
            <Plus className="w-4 h-4" /> Add Number
          </button>
        )}
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Kpi label="Total Numbers" v={summary.total} icon={Smartphone} bg="#D8F3DC" accent="#1B4332" />
        <Kpi label="Assigned" v={summary.assigned} icon={CheckCircle2} bg="#DCE9F0" accent="#1F4863" />
        <Kpi label="Available Pool" v={summary.available} icon={CircleDashed} bg="#FCE7C9" accent="#9A6020" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {POOLS.map((p) => {
          const ps = summary.pools?.[p.value] || { total: 0, assigned: 0, available: 0 };
          return (
            <div key={p.value} className="card-flat p-4" data-testid={`pool-card-${p.value}`}>
              <div className="flex items-center justify-between">
                <div className="font-heading font-bold text-base" style={{ color: "var(--color-primary)" }}>{p.label} Pool</div>
                <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: "#F4F1DE", color: "#57534E" }}>
                  {ps.total} SIM{ps.total === 1 ? "" : "s"}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
                <div><div className="kpi-label">Assigned</div><div className="font-semibold mt-0.5" style={{ color: "#1F4863" }}>{ps.assigned}</div></div>
                <div><div className="kpi-label">Available</div><div className="font-semibold mt-0.5" style={{ color: "#1B4332" }}>{ps.available}</div></div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-end gap-3 flex-wrap">
        <label className="flex flex-col gap-1 flex-1 min-w-[14rem] max-w-md">
          <span className="kpi-label">Search</span>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Number, provider, assignee…"
                   data-testid="mobile-search"
                   className="w-full pl-9 pr-3 py-2 rounded-md border border-stone-200 text-sm bg-white" />
          </div>
        </label>
        <label className="flex flex-col gap-1">
          <span className="kpi-label">Pool</span>
          <select value={pool} onChange={(e) => setPool(e.target.value)} data-testid="filter-pool"
                  className="px-3 py-2 rounded-md border border-stone-200 text-sm bg-white font-medium min-w-[12rem]">
            <option value="">All Pools</option>
            {POOLS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="kpi-label">Status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="filter-status"
                  className="px-3 py-2 rounded-md border border-stone-200 text-sm bg-white font-medium min-w-[10rem]">
            <option value="">All Statuses</option>
            <option value="available">Available</option>
            <option value="assigned">Assigned</option>
            <option value="lost">Lost</option>
            <option value="inactive">Inactive</option>
          </select>
        </label>
      </div>

      <div className="card-flat p-0 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-stone-500 text-sm" data-testid="mobile-empty">
            <Smartphone className="w-10 h-10 mx-auto mb-3 text-stone-300" />
            No mobile numbers
            {canManage && <div className="mt-2 text-xs">Click &quot;Add Number&quot; to start building your SIM pool.</div>}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="mobile-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-stone-500 bg-stone-50/60 border-b border-stone-200">
                  <th className="px-4 py-3">Number</th>
                  <th>Pool</th>
                  <th>Provider · Plan</th>
                  <th className="text-right">Rental</th>
                  <th>Status</th>
                  <th>Assigned To</th>
                  <th>Period</th>
                  <th>Remarks</th>
                  {canManage && <th className="px-3"></th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map((m) => {
                  const s = STATUS_STYLE[m.status] || STATUS_STYLE.available;
                  const poolLabel = POOLS.find((p) => p.value === m.pool)?.label || m.pool;
                  return (
                    <tr key={m.id} className="border-t border-stone-100 hover:bg-stone-50/40" data-testid={`mobile-row-${m.id}`}>
                      <td className="px-4 py-2.5 font-semibold tabular-nums tracking-wide">{m.number}</td>
                      <td><span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: "#F4F1DE", color: "#57534E" }}>{poolLabel}</span></td>
                      <td className="text-xs text-stone-700">
                        <div className="font-medium">{m.provider || "—"}</div>
                        <div className="text-stone-500">{m.plan || "—"}</div>
                      </td>
                      <td className="text-right text-xs">{m.monthly_rental ? formatINR(m.monthly_rental) : "—"}</td>
                      <td><span className="status-pill" style={{ background: s.bg, color: s.color }}>{s.label}</span></td>
                      <td>
                        {m.assigned_to_name ? (
                          <div>
                            <div className="font-medium text-stone-800">{m.assigned_to_name}</div>
                            <div className="text-[11px] text-stone-500">{m.assigned_to_designation || ""}</div>
                          </div>
                        ) : (
                          <span className="text-stone-400 text-xs italic">Unassigned</span>
                        )}
                      </td>
                      <td className="text-xs text-stone-600 whitespace-nowrap">
                        {m.assigned_date && <div>From {m.assigned_date}</div>}
                        {m.return_date && <div>Until {m.return_date}</div>}
                        {!m.assigned_date && !m.return_date && "—"}
                      </td>
                      <td className="text-xs text-stone-500 max-w-[14rem] truncate italic">{m.remarks || "—"}</td>
                      {canManage && (
                        <td className="px-3 whitespace-nowrap">
                          <div className="flex gap-1">
                            <button onClick={() => { setEditing(m); setShow(true); }} title="Edit"
                                    data-testid={`mobile-edit-${m.id}`}
                                    className="p-1.5 rounded-md hover:bg-stone-100"><Edit2 className="w-3.5 h-3.5" /></button>
                            <button onClick={() => remove(m.id)} title="Delete"
                                    data-testid={`mobile-delete-${m.id}`}
                                    className="p-1.5 rounded-md hover:bg-red-50 text-red-700"><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {show && (
        <MobileNumberModal
          employees={employees}
          record={editing}
          onClose={() => { setShow(false); setEditing(null); }}
          onSaved={() => { setShow(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function Kpi({ label, v, icon: Icon, bg, accent }) {
  return (
    <div className="card-flat p-4 flex items-center gap-3">
      <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: bg }}>
        <Icon className="w-5 h-5" style={{ color: accent }} />
      </div>
      <div>
        <div className="kpi-label">{label}</div>
        <div className="font-heading text-2xl font-bold mt-0.5" style={{ color: accent }}>{v}</div>
      </div>
    </div>
  );
}

function MobileNumberModal({ employees, record, onClose, onSaved }) {
  const isEdit = !!record;
  const [form, setForm] = useState({
    number: record?.number || "",
    pool: record?.pool || "NAVARATNAA",
    provider: record?.provider || "",
    plan: record?.plan || "",
    monthly_rental: record?.monthly_rental || 0,
    status: record?.status || "available",
    assigned_to: record?.assigned_to || "",
    assigned_date: record?.assigned_date || "",
    return_date: record?.return_date || "",
    remarks: record?.remarks || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const payload = {
        number: form.number.trim(),
        pool: form.pool,
        provider: form.provider,
        plan: form.plan,
        monthly_rental: Number(form.monthly_rental || 0),
        status: form.status,
        assigned_to: form.assigned_to || null,
        assigned_date: form.assigned_date || null,
        return_date: form.return_date || null,
        remarks: form.remarks,
      };
      if (isEdit) await api.put(`/mobile-numbers/${record.id}`, payload);
      else await api.post("/mobile-numbers", payload);
      onSaved();
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/50 flex items-center justify-center p-4 overflow-y-auto" data-testid="mobile-modal">
      <form onSubmit={submit} className="bg-white rounded-xl w-full max-w-xl p-6 my-8 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-xl font-bold">{isEdit ? "Edit Mobile Number" : "Add Mobile Number"}</h2>
          <button type="button" onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        {err && <div className="p-3 rounded-md text-sm" style={{ background: "#FADED7", color: "#9A3412" }} data-testid="mobile-modal-error">{err}</div>}

        <In label="Mobile Number" value={form.number} onChange={(v) => setForm({ ...form, number: v })}
            placeholder="+91 98765 43210" required testid="modal-number" />

        <div className="grid grid-cols-2 gap-3">
          <Select label="Pool" value={form.pool} onChange={(v) => setForm({ ...form, pool: v })} testid="modal-pool">
            {POOLS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </Select>
          <Select label="Status" value={form.status} onChange={(v) => setForm({ ...form, status: v })} testid="modal-status">
            <option value="available">Available</option>
            <option value="assigned">Assigned</option>
            <option value="lost">Lost</option>
            <option value="inactive">Inactive</option>
          </Select>
          <In label="Provider" value={form.provider} onChange={(v) => setForm({ ...form, provider: v })} placeholder="Airtel" />
          <In label="Plan" value={form.plan} onChange={(v) => setForm({ ...form, plan: v })} placeholder="Postpaid 399" />
          <In label="Monthly Rental (₹)" type="number" value={form.monthly_rental} onChange={(v) => setForm({ ...form, monthly_rental: v })} placeholder="399" />
        </div>

        <Select label="Assigned To" value={form.assigned_to} onChange={(v) => setForm({ ...form, assigned_to: v })} testid="modal-assignee">
          <option value="">Unassigned</option>
          {employees.map((e) => (
            <option key={e.id} value={e.id}>{e.full_name}{e.designation ? ` · ${e.designation}` : ""}</option>
          ))}
        </Select>

        <div className="grid grid-cols-2 gap-3">
          <In label="Assigned Date" type="date" value={form.assigned_date} onChange={(v) => setForm({ ...form, assigned_date: v })} />
          <In label="Return Date" type="date" value={form.return_date} onChange={(v) => setForm({ ...form, return_date: v })} />
        </div>

        <In label="Remarks" value={form.remarks} onChange={(v) => setForm({ ...form, remarks: v })} placeholder="Optional" />

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-md border border-stone-200 text-sm">Cancel</button>
          <button type="submit" disabled={busy} data-testid="modal-save"
                  className="px-5 py-2 rounded-md text-white font-medium text-sm" style={{ background: "var(--color-primary)" }}>
            {busy ? "Saving…" : (isEdit ? "Save Changes" : "Save")}
          </button>
        </div>
      </form>
    </div>
  );
}

function In({ label, type = "text", value, onChange, required, placeholder, testid }) {
  return (
    <label className="block">
      <div className="kpi-label mb-1.5">{label}</div>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} placeholder={placeholder}
             data-testid={testid}
             className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm focus:outline-none focus:border-primary" />
    </label>
  );
}

function Select({ label, value, onChange, children, testid }) {
  return (
    <label className="block">
      <div className="kpi-label mb-1.5">{label}</div>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
              className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm bg-white focus:outline-none focus:border-primary">
        {children}
      </select>
    </label>
  );
}
